/**
 * Camera helpers for the picker — a faithful port of the desktop tool's
 * semantics (nyc_ingest backend/services/captures.py).
 *
 *  - SN/camera display is a 4-digit zero-pad ("0094").
 *  - resolveCamera mirrors `resolve_camera` (captures.py:279): when a template
 *    camera is missing from a given capture, search UPWARD from the target, then
 *    DOWNWARD, returning the first camera that exists. The swap is surfaced
 *    (substituted:true) so the UI can badge it amber — the desktop tool computed
 *    this (`had_substitutions`) but never showed it (design package §1b.6).
 *  - A preset / applied template is ALWAYS exactly 6 cameras, first = key.
 *
 * v1 thumbnails are gray-box placeholders generated client-side from the camera
 * number; the bridge "preview" module uploads real ~150px thumbs to Storage in
 * slice 2 (design package Q2-A). Rotation (sheetsConfig/rotations_{rigType}) is
 * applied at display time, exactly as the desktop tool applies it at serve time.
 */

export const pad4 = (n) => String(n).padStart(4, "0");

/**
 * Deterministic signature of a camera selection — the unique, sorted cameras
 * joined by "-" (mirror of functions/sheetThumbs.ts selectionSignature). Used by
 * the Proof gate (D1) to verify embed readiness against the CURRENT picks rather
 * than trusting the stored embedsReady boolean.
 */
export const selectionSignature = (selected) =>
  [...new Set(selected || [])].sort((a, b) => a - b).join("-");

/** D1: a capture's embeds are usable only if ready AND built for the current picks. */
export const captureEmbedReady = (capture) =>
  !!capture?.embedsReady && (capture?.embedSignature ?? "") === selectionSignature(capture?.selected);

/** Resolve one template camera against the cameras a capture actually has. */
export function resolveCamera(target, availableCameras) {
  const set = availableCameras instanceof Set ? availableCameras : new Set(availableCameras);
  if (set.has(target)) return { camera: target, substituted: false };
  const max = set.size ? Math.max(...set) : target;
  for (let c = target + 1; c <= max; c++) if (set.has(c)) return { camera: c, substituted: true };
  for (let c = target - 1; c >= 1; c--) if (set.has(c)) return { camera: c, substituted: true };
  return { camera: target, substituted: false }; // nothing available — leave the hole
}

/**
 * Apply a 6-camera template to one capture. Returns the resolved slot list, the
 * key camera, and which resolved slots were substituted (by slot index).
 */
export function resolveTemplate(template, availableCameras) {
  const set = new Set(availableCameras);
  const selected = [];
  const substitutedSlots = new Set();
  template.forEach((cam, i) => {
    const r = resolveCamera(cam, availableCameras instanceof Set ? availableCameras : set);
    selected.push(r.camera);
    if (r.substituted) substitutedSlots.add(i);
  });
  return {
    selected,
    keyCamera: selected[0] ?? null,
    substitutedSlots,
    hadSubstitutions: substitutedSlots.size > 0,
  };
}

const THUMB_HUES = ["#DDD9D3", "#D6D1C9", "#E2DCD2", "#D2CEC6"];

/** A deterministic gray-box placeholder for a camera, with its number rendered. */
export function thumbDataUri(camera, rotation = 0) {
  const bg = THUMB_HUES[camera % THUMB_HUES.length];
  const label = pad4(camera);
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='80' height='120' viewBox='0 0 80 120'>` +
    `<rect width='80' height='120' fill='${bg}'/>` +
    `<rect x='6' y='6' width='68' height='108' fill='none' stroke='#C4BFB6' stroke-width='1'/>` +
    `<text x='40' y='64' font-family='monospace' font-size='15' fill='#8A857C' ` +
    `text-anchor='middle' transform='rotate(${rotation} 40 60)'>${label}</text>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
